import { useState, useEffect } from 'react'
import { useNavigate, useSearchParams } from 'react-router-dom'
import { supabase } from '../lib/supabase'
import { useAuth } from '../contexts/AuthContext'
import { useToast } from '@/hooks/use-toast'
import { Loader2 } from 'lucide-react'
import { PLANS } from '@/data/plans'
import PricingPlans from './pricing/PricingPlans'

export default function Pricing() {
  const { user, isAuthenticated } = useAuth()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const { toast } = useToast()

  const [profile, setProfile] = useState(null)
  const [loadingProfile, setLoadingProfile] = useState(true)
  const [subscribing, setSubscribing] = useState(null)
  const [managingPortal, setManagingPortal] = useState(false)
  // Default to annual (best value) only once annual Stripe prices exist.
  const [billing, setBilling] = useState(PLANS.every((p) => p.annualPriceId) ? 'annual' : 'monthly')

  useEffect(() => {
    if (searchParams.get('subscribed') === 'true') {
      toast({
        title: 'Subscription activated!',
        description: 'Welcome to Kinora. Your slices have been granted.',
      })
    }
  }, [])

  useEffect(() => {
    if (isAuthenticated && user) {
      fetchProfile()
    } else {
      setLoadingProfile(false)
    }
  }, [isAuthenticated, user])

  const fetchProfile = async () => {
    const { data } = await supabase
      .from('profiles')
      .select('subscription_tier, subscription_status, subscription_current_period_end')
      .eq('id', user.id)
      .single()

    setProfile(data)
    setLoadingProfile(false)
  }

  const isSubscribed =
    profile?.subscription_status === 'active' &&
    profile?.subscription_tier != null &&
    !['New User', 'None'].includes(profile.subscription_tier)

  useEffect(() => {
    if (!loadingProfile && isSubscribed) {
      navigate('/profile?tab=account', { replace: true })
    }
  }, [loadingProfile, isSubscribed, navigate])

  const handleSubscribe = async (plan) => {
    if (!isAuthenticated) {
      navigate('/auth', { state: { returnTo: '/pricing' } })
      return
    }

    const isAnnual = billing === 'annual'
    const priceId = isAnnual ? plan.annualPriceId : plan.monthlyPriceId

    // Annual Stripe prices don't exist yet — offer monthly instead of dead-ending.
    if (!priceId) {
      toast({
        title: 'Annual billing launching soon',
        description: `Annual ${plan.tier} isn't available yet — monthly is ready today and you can switch later.`,
      })
      setBilling('monthly')
      return
    }

    setSubscribing(plan.tier)
    try {
      const { data, error } = await supabase.functions.invoke('create-checkout-session', {
        body: {
          price_id: priceId,
          success_url: `${window.location.origin}/browse?subscribed=true`,
          cancel_url: `${window.location.origin}/pricing`,
        },
      })
      if (error) throw error
      if (data?.checkout_url) {
        window.location.href = data.checkout_url
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to start checkout. Please try again.',
        variant: 'destructive',
      })
    } finally {
      setSubscribing(null)
    }
  }

  const handleManageSubscription = async () => {
    setManagingPortal(true)
    try {
      const { data, error } = await supabase.functions.invoke('create-billing-portal', {
        body: { return_url: `${window.location.origin}/pricing` },
      })
      if (error) throw error
      if (data?.portal_url) {
        window.location.href = data.portal_url
      }
    } catch (err) {
      toast({
        title: 'Error',
        description: err.message || 'Failed to open subscription portal.',
        variant: 'destructive',
      })
    } finally {
      setManagingPortal(false)
    }
  }

  if (loadingProfile || isSubscribed) {
    return (
      <div className="flex items-center justify-center min-h-[500px]">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  const ctx = {
    plans: PLANS, billing, setBilling, isAnnual: billing === 'annual', isSubscribed, profile,
    subscribing, managingPortal, handleSubscribe, handleManageSubscription,
  }
  return <PricingPlans ctx={ctx} />
}
